import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import {
  ArrowUpRight,
  ArrowLeft,
  MapPin,
  Mail,
  Youtube,
  Phone,
  Clock,
  CheckCircle2,
  MessageCircle,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import QuickActions from "../components/QuickActions";
import {
  TEL_HREF,
  MAIL_HREF,
  MAIL_PRO_HREF,
  WA_HREF,
  PHONE_DISPLAY,
  EMAIL,
  EMAIL_PRO,
} from "../components/QuickActions";
import {
  trackPhoneClick,
  trackEmailClick,
  trackWhatsAppClick,
  trackFormSubmit,
} from "../lib/tracking";

const ease = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = {
  fr: [
    "BIM / Revit",
    "Dossier AT",
    "Aménagement bureaux",
    "ERP / IGH",
    "Scan 3D / nuage de points",
    "Rénovation",
    "Autre",
  ],
  en: [
    "BIM / Revit",
    "Technical file (AT)",
    "Office layout",
    "ERP / IGH",
    "3D scan / point cloud",
    "Renovation",
    "Other",
  ],
};

const BUDGET_RANGES = {
  fr: [
    "< 50 000 €",
    "50 000 — 150 000 €",
    "150 000 — 500 000 €",
    "500 000 — 1 M€",
    "> 1 M€",
    "À définir",
  ],
  en: [
    "< €50k",
    "€50k — €150k",
    "€150k — €500k",
    "€500k — €1M",
    "> €1M",
    "To be defined",
  ],
};

export default function ContactPage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    projectType: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus("sending");

    const meta = [
      form.phone && `Tel: ${form.phone}`,
      form.projectType && `Type: ${form.projectType}`,
    ]
      .filter(Boolean)
      .join(" · ");

    const fullMessage = meta ? `[${meta}]\n\n${form.message}` : form.message;

    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        subject: form.projectType || (lang === "fr" ? "Nouveau projet" : "New project"),
        message: fullMessage,
        locale: lang,
      });
      trackFormSubmit("contact-page");
      // Redirect to /merci for Google Ads conversion tracking
      navigate("/merci");
    } catch (err) {
      console.error(err);
      setStatus("err");
    }
  };

  return (
    <div className="bg-[#F7F7F9] text-[#0A0A0A] min-h-screen antialiased selection:bg-[#D35400] selection:text-white">
      <Header />

      <main className="pt-24 md:pt-32">
        {/* Hero strip */}
        <section className="relative overflow-hidden border-b border-[#E5E5E5]">
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.035] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(to right, #0A0A0A 1px, transparent 1px), linear-gradient(to bottom, #0A0A0A 1px, transparent 1px)",
              backgroundSize: "80px 80px",
            }}
          />
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-14 md:py-24 relative">
            {/* Breadcrumb */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease }}
              className="flex items-center gap-3 mb-10"
            >
              <Link
                to="/"
                data-testid="back-home-link"
                className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] hover:text-[#D35400] transition-colors"
              >
                <ArrowLeft size={12} />
                {lang === "fr" ? "Studio" : "Studio"}
              </Link>
              <span className="text-[#CCC]">/</span>
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#0A0A0A]">
                {t.contact.overline}
              </span>
            </motion.div>

            {/* Massive title */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease, delay: 0.1 }}
              className="font-display font-medium tracking-tighter leading-[0.88] text-[clamp(3rem,11vw,11rem)] max-w-[14ch]"
            >
              {lang === "fr" ? (
                <>
                  Démarrons votre <span className="italic font-normal text-[#5C5C5C]">projet</span>
                  <span className="text-[#D35400]">.</span>
                </>
              ) : (
                <>
                  Start your <span className="italic font-normal text-[#5C5C5C]">project</span>
                  <span className="text-[#D35400]">.</span>
                </>
              )}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.25 }}
              className="mt-10 max-w-[58ch] text-lg md:text-xl font-light leading-relaxed text-[#0A0A0A]"
            >
              {lang === "fr"
                ? "Vous avez un projet de bureaux, d'aménagement, de rénovation ou un besoin en BIM / Revit ? Contactez-moi pour cadrer votre demande et recevoir un devis professionnel — réponse personnelle sous 48h ouvrées."
                : "Office layout, fit-out, renovation or a BIM / Revit need? Get in touch — I'll help frame your request and send a professional quote. Personal reply within 48 working hours."}
            </motion.p>

            {/* Quick contact actions on top of the page */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease, delay: 0.35 }}
              className="mt-10"
            >
              <QuickActions variant="inline" source="contact-hero" />
            </motion.div>
          </div>
        </section>

        {/* Two-column block */}
        <section className="bg-[#F7F7F9] py-14 md:py-24">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            <div className="grid grid-cols-12 gap-10 md:gap-16">
              {/* Left col — info */}
              <aside className="col-span-12 md:col-span-4 lg:col-span-4 md:sticky md:top-24 h-fit space-y-12">
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mb-6">
                    {lang === "fr" ? "Coordonnées directes" : "Direct lines"}
                  </div>

                  <ul className="space-y-7">
                    <li>
                      <a
                        data-testid="contact-page-phone"
                        href={TEL_HREF}
                        onClick={() => trackPhoneClick("contact-page")}
                        className="flex items-start gap-4 group"
                      >
                        <Phone size={16} className="text-[#D35400] mt-1" />
                        <div>
                          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-1">
                            {t.contact.phone}
                          </div>
                          <div className="text-base md:text-lg font-light group-hover:text-[#D35400] transition-colors">
                            {PHONE_DISPLAY}
                          </div>
                        </div>
                      </a>
                    </li>
                    <li>
                      <a
                        data-testid="contact-page-email"
                        href={MAIL_HREF}
                        onClick={() => trackEmailClick("contact-page")}
                        className="flex items-start gap-4 group"
                      >
                        <Mail size={16} className="text-[#D35400] mt-1" />
                        <div>
                          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-1">
                            Email principal
                          </div>
                          <div className="text-base md:text-lg font-light group-hover:text-[#D35400] transition-colors">
                            {EMAIL}
                          </div>
                        </div>
                      </a>
                    </li>
                    <li>
                      <a
                        data-testid="contact-page-email-pro"
                        href={MAIL_PRO_HREF}
                        onClick={() => trackEmailClick("contact-page-pro")}
                        className="flex items-start gap-4 group"
                      >
                        <Mail size={16} className="text-[#D35400] mt-1" />
                        <div>
                          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-1">
                            {lang === "fr" ? "Ordre des Architectes" : "Architects' Order"}
                          </div>
                          <div className="text-base md:text-lg font-light group-hover:text-[#D35400] transition-colors break-all">
                            {EMAIL_PRO}
                          </div>
                        </div>
                      </a>
                    </li>
                    <li>
                      <a
                        data-testid="contact-page-whatsapp"
                        href={WA_HREF}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => trackWhatsAppClick("contact-page")}
                        className="flex items-start gap-4 group"
                      >
                        <MessageCircle size={16} className="text-[#25D366] mt-1" />
                        <div>
                          <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-1">
                            WhatsApp
                          </div>
                          <div className="text-base md:text-lg font-light group-hover:text-[#25D366] transition-colors">
                            {lang === "fr" ? "Discuter sur WhatsApp" : "Chat on WhatsApp"}
                          </div>
                        </div>
                      </a>
                    </li>
                    <li className="flex items-start gap-4">
                      <MapPin size={16} className="text-[#D35400] mt-1" />
                      <div>
                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-1">
                          {t.contact.based}
                        </div>
                        <div className="text-base md:text-lg font-light">{t.contact.area}</div>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <Clock size={16} className="text-[#D35400] mt-1" />
                      <div>
                        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-1">
                          {lang === "fr" ? "Disponibilité" : "Availability"}
                        </div>
                        <div className="text-base md:text-lg font-light">
                          {lang === "fr" ? "Lun. — Ven. · 9h — 19h" : "Mon. — Fri. · 9am — 7pm"}
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>

                {/* Why us / promise */}
                <div className="border-t border-[#E5E5E5] pt-10">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C] mb-6">
                    {lang === "fr" ? "Nos engagements" : "Our promise"}
                  </div>
                  <ul className="space-y-4 text-sm md:text-base">
                    {(lang === "fr"
                      ? [
                          "Réponse personnelle sous 48h ouvrées",
                          "Étude de faisabilité gratuite & confidentielle",
                          "Maquette BIM dès la phase esquisse",
                        ]
                      : [
                          "Personal reply within 48 working hours",
                          "Free & confidential feasibility study",
                          "BIM model from the sketch phase",
                        ]
                    ).map((p, i) => (
                      <li key={i} className="flex items-start gap-3 font-light">
                        <CheckCircle2 size={16} className="text-[#D35400] mt-1 shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>

              {/* Right col — Form */}
              <div className="col-span-12 md:col-span-8 lg:col-span-8 md:pl-8 lg:pl-16">
                {status === "err" && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-10 border border-red-500 bg-red-50 p-6 md:p-8"
                    data-testid="contact-page-error"
                  >
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-red-600 mb-2">
                      ✗ {lang === "fr" ? "Erreur" : "Error"}
                    </div>
                    <p className="font-light">{t.contact.error}</p>
                  </motion.div>
                )}

                <form data-testid="contact-page-form" onSubmit={submit} className="space-y-10">
                  {/* Section: about you */}
                  <FormSection number="01" title={lang === "fr" ? "Vous" : "About you"}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <Field
                        id="cp-name"
                        label={t.contact.name}
                        value={form.name}
                        onChange={update("name")}
                        required
                        testid="cp-name"
                      />
                      <Field
                        id="cp-email"
                        type="email"
                        label={t.contact.email}
                        value={form.email}
                        onChange={update("email")}
                        required
                        testid="cp-email"
                      />
                      <Field
                        id="cp-phone"
                        type="tel"
                        label={t.contact.phone}
                        value={form.phone}
                        onChange={update("phone")}
                        testid="cp-phone"
                      />
                      <SelectField
                        id="cp-projectType"
                        label={lang === "fr" ? "Type de projet" : "Project type"}
                        value={form.projectType}
                        onChange={update("projectType")}
                        options={PROJECT_TYPES[lang]}
                        placeholder={lang === "fr" ? "— Sélectionner —" : "— Select —"}
                        testid="cp-projectType"
                      />
                    </div>
                  </FormSection>

                  {/* Section: project message */}
                  <FormSection number="02" title={lang === "fr" ? "Votre projet" : "Your project"}>
                    <Field
                      id="cp-message"
                      label={
                        lang === "fr"
                          ? "Décrivez votre projet — localisation, surface, contraintes, échéance"
                          : "Describe your project — location, area, constraints, timeline"
                      }
                      value={form.message}
                      onChange={update("message")}
                      required
                      multiline
                      rows={7}
                      testid="cp-message"
                    />
                  </FormSection>

                  <div className="pt-4 flex flex-col md:flex-row md:items-center justify-between gap-6 border-t border-[#E5E5E5]">
                    <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] max-w-md">
                      {lang === "fr"
                        ? "Vos informations restent strictement confidentielles."
                        : "Your information remains strictly confidential."}
                    </p>
                    <button
                      type="submit"
                      disabled={status === "sending"}
                      data-testid="cp-submit"
                      className="group inline-flex items-center justify-center gap-3 bg-[#0A0A0A] text-[#F7F7F9] px-10 py-6 text-sm tracking-[0.18em] uppercase font-mono hover:bg-[#D35400] transition-colors duration-500 disabled:opacity-60"
                    >
                      {status === "sending"
                        ? t.contact.sending
                        : lang === "fr"
                        ? "Envoyer ma demande"
                        : "Send my request"}
                      <ArrowUpRight
                        size={18}
                        className="group-hover:rotate-45 transition-transform duration-500"
                      />
                    </button>
                  </div>

                  {status === "err" && (
                    <p
                      data-testid="contact-page-error"
                      className="font-mono text-xs tracking-[0.22em] uppercase text-red-600"
                    >
                      ✗ {t.contact.error}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* ---------- helpers ---------- */
function InfoRow({ icon, label, value, href, external, testid }) {
  const inner = (
    <>
      <span className="mt-1">{icon}</span>
      <div>
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] mb-1">
          {label}
        </div>
        <div className="text-base md:text-lg font-light group-hover:text-[#D35400] transition-colors">
          {value}
        </div>
      </div>
    </>
  );
  if (href) {
    return (
      <li>
        <a
          data-testid={testid}
          href={href}
          {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
          className="flex items-start gap-4 group"
        >
          {inner}
        </a>
      </li>
    );
  }
  return (
    <li className="flex items-start gap-4">
      {inner}
    </li>
  );
}

function FormSection({ number, title, children }) {
  return (
    <fieldset className="border-t border-[#E5E5E5] pt-10">
      <legend className="flex items-baseline gap-3 mb-10">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C]">
          {number}
        </span>
        <span className="font-display text-2xl md:text-3xl tracking-tight">{title}</span>
      </legend>
      {children}
    </fieldset>
  );
}

function Field({ id, label, value, onChange, type = "text", required, multiline, rows = 5, testid }) {
  return (
    <div className="relative">
      <label
        htmlFor={id}
        className="absolute -top-3 left-0 font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] bg-[#F7F7F9] px-1"
      >
        {label}
        {required ? " *" : ""}
      </label>
      {multiline ? (
        <textarea
          id={id}
          data-testid={testid}
          value={value}
          onChange={onChange}
          required={required}
          rows={rows}
          className="w-full bg-transparent border-b border-[#0A0A0A]/30 focus:border-[#D35400] outline-none py-3 text-base text-[#0A0A0A] placeholder:text-[#B5B5B5] transition-colors resize-none"
        />
      ) : (
        <input
          id={id}
          data-testid={testid}
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          className="w-full bg-transparent border-b border-[#0A0A0A]/30 focus:border-[#D35400] outline-none py-3 text-base text-[#0A0A0A] placeholder:text-[#B5B5B5] transition-colors"
        />
      )}
    </div>
  );
}

function SelectField({ id, label, value, onChange, options, placeholder, testid }) {
  return (
    <div className="relative">
      <label
        htmlFor={id}
        className="absolute -top-3 left-0 font-mono text-[10px] tracking-[0.22em] uppercase text-[#5C5C5C] bg-[#F7F7F9] px-1"
      >
        {label}
      </label>
      <select
        id={id}
        data-testid={testid}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-b border-[#0A0A0A]/30 focus:border-[#D35400] outline-none py-3 text-base text-[#0A0A0A] transition-colors appearance-none cursor-pointer"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      <span className="absolute right-2 top-3 pointer-events-none text-[#5C5C5C]">▾</span>
    </div>
  );
}
